import { DiscountAchieverBlock, DiscountAchieverVariant } from "lib/types/discountAchiever";
import { currencyFormatter } from "lib/utils/formatters";

export interface BlockFulfillment {
  current: number;
  target: number;
  isFulfilled: boolean;
  /** Fractional completion in [0, 1] — current/target capped at 1, so partial amounts show partial progress */
  progress: number;
}

const toProgress = (current: number, target: number): number =>
  target > 0 ? Math.min(current / target, 1) : 1;

export const computeBlockFulfillment = (
  block: DiscountAchieverBlock,
  selectedItems: Record<string, number>,
  selectedMeta: Record<string, { variant: DiscountAchieverVariant }> = {},
): BlockFulfillment => {
  const { missing_quantity, missing_amount } = block.impacted_entity;

  if (missing_amount !== null) {
    const priceByVariantId = new Map<string, number>();
    block.products.forEach((product) => {
      product.variants.forEach((variant) => {
        priceByVariantId.set(variant.variant_id, variant.discounted_price ?? variant.current_price);
      });
    });
    const getPrice = (variantId: string): number => {
      const livePrice = priceByVariantId.get(variantId);
      if (livePrice !== undefined) return livePrice;
      const variant = selectedMeta[variantId]?.variant;
      return variant ? (variant.discounted_price ?? variant.current_price) : 0;
    };
    const current = Object.entries(selectedItems).reduce(
      (sum, [variantId, qty]) => sum + getPrice(variantId) * qty,
      0,
    );

    return {
      current,
      target: missing_amount,
      isFulfilled: current >= missing_amount,
      progress: toProgress(current, missing_amount),
    };
  }

  const target = missing_quantity ?? 0;
  const current = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);

  return { current, target, isFulfilled: current >= target, progress: toProgress(current, target) };
};

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/** Per-block label shown above the grid ("Add 2 products" / "Add products worth ₹500 from the block") */
export const getBlockRemainingMessage = (block: DiscountAchieverBlock, fulfillment: BlockFulfillment): string => {
  if (fulfillment.isFulfilled) return '';

  const remaining = Math.max(fulfillment.target - fulfillment.current, 0);

  return block.impacted_entity.missing_amount !== null
    ? `Add products worth ${currencyFormatter(remaining)} from the collection`
    : `Add ${pluralize(remaining, "product")}`;
};

export interface RemainingToShopMessagePart {
  text: string;
  /** Renders in the merchant's primary text color — the "3 items" / "items worth ₹500" clause */
  highlight?: boolean;
}

/**
 * Header subtitle for the still-to-shop state. Blocks not yet fulfilled are split by their
 * impacted_entity type — quantity-based ("3 items") vs amount-based ("items worth ₹500") — and
 * both clauses are joined when a tray mixes both discount shapes across its collections.
 */
export const getRemainingToShopMessage = (
  blocks: DiscountAchieverBlock[],
  fulfillments: BlockFulfillment[],
): RemainingToShopMessagePart[] => {
  const pending = blocks
    .map((block, index) => ({ block, fulfillment: fulfillments[index] }))
    .filter(({ fulfillment }) => fulfillment && !fulfillment.isFulfilled);

  if (pending.length === 0) return [{ text: "Add items to unlock your exclusive discount" }];

  const collectionsClause = pluralize(pending.length, "collection");

  const remainingQuantity = pending
    .filter(({ block }) => block.impacted_entity.missing_amount === null)
    .reduce((sum, { fulfillment }) => sum + Math.max(fulfillment.target - fulfillment.current, 0), 0);

  const remainingAmount = pending
    .filter(({ block }) => block.impacted_entity.missing_amount !== null)
    .reduce((sum, { fulfillment }) => sum + Math.max(fulfillment.target - fulfillment.current, 0), 0);

  const clauses = [
    remainingQuantity > 0 ? pluralize(remainingQuantity, "item") : null,
    remainingAmount > 0 ? `items worth ${currencyFormatter(remainingAmount)}` : null,
  ].filter(Boolean);

  return [
    { text: "Shop " },
    { text: clauses.join(" and "), highlight: true },
    { text: ` from ${collectionsClause} to avail` },
  ];
};
